import { useRef, useState } from 'react';
import cn from 'classnames';

import useOutsideClick from 'js/hooks/use-outside-click';
import { defineTheme, type ThemeKeys } from 'js/utils/theme-helpers';

import Button from 'components/button/Button';
import Icon, { type IconName } from 'components/icon/Icon';
import ShareOptions from 'components/share-options/ShareOptions';
import DownloadList from 'components/download-list/DownloadList';
import TabTrapper from 'components/modal/TabTrapper';

import { IOptionsModal } from 'source/NFR.types';

const themes = defineTheme({
  isArticleHeader: 'options-modal--is-article-header',
  isProposalOrEvent: 'options-modal--is-proposal-or-event',
  isTextWithSidebar: 'options-modal--is-text-with-sidebar',
  isPageFooter: 'options-modal--is-page-footer'
});

type Theme = ThemeKeys<typeof themes>;

interface OptionsModalProps extends IOptionsModal {
  theme?: Theme;
  className?: string;
}

const OptionsModal: React.FC<OptionsModalProps> = ({
  theme,
  openButtonText,
  closeButtonLabel,
  shareContent,
  downloadContent,
  className
}) => {
  const [show, setShow] = useState(false);
  const openModal = (): void => setShow(true);
  const closeModal = (): void => setShow(false);

  const clickOutsideRef = useRef<HTMLDivElement>(null);
  useOutsideClick(clickOutsideRef, () => {
    closeModal();
  });

  const iconBeforeChildren = (theme?: Theme): IconName | undefined => {
    if (theme === themes.isTextWithSidebar) return 'share-small';
    if (theme === themes.isArticleHeader) return 'share-new-profile-small';
    return undefined;
  };

  return (
    <div ref={clickOutsideRef} className={cn('options-modal', themes(theme), className)}>
      <div className="options-modal__container">
        <Button
          iconBeforeChildren={iconBeforeChildren(theme)}
          icon={
            theme === themes.isPageFooter || !theme
              ? 'share-small'
              : theme === themes.isProposalOrEvent
                ? 'arrow-down'
                : undefined
          }
          className={cn('options-modal__button', show && 'options-modal__button--is-open')}
          onClick={show ? closeModal : openModal}
        >
          {openButtonText}
        </Button>
        <TabTrapper isActive={show}>
          <div className={cn(show ? 'options-modal__modal-container' : 'options-modal__modal-container--hidden')}>
            <Button title={closeButtonLabel} className="options-modal__close-button" onClick={closeModal}>
              <Icon className="options-modal__close-button--icon" name="close-small" />
            </Button>
            {shareContent && <ShareOptions {...shareContent} />}
            {downloadContent && (
              <div className="options-modal__download-list">
                <DownloadList {...downloadContent} />
              </div>
            )}
          </div>
        </TabTrapper>
      </div>
    </div>
  );
};

OptionsModal.displayName = 'OptionsModal';

export default OptionsModal;
